import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  selectCurrentCamera,
  selectIsPlanogramSourceImageVisible,
  selectSelectedPolygon,
  setSelectedPolygon,
  setSelectedProduct,
  setSelectedSkuSectionId,
} from "@/entities/annotation/model/slices/slice";
import { selectSelectedGondola } from "@/entities/annotation";
import CameraSkuSectionImage from "@/features/annotation/ui/camera-sku-section-image";
import SkuSectionPolygons from "./sku-section-polygons";
import {
  SkuSection,
  useGondolasSkuSectionsQuery,
} from "@/shared/api/insights-api";
import { getCameraSkuSections } from "../hooks/useCameraData";
import { WithIndex } from "@/shared/api";
import { Vector2d } from "konva/lib/types";
import { Html } from "react-konva-utils";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import { twMerge } from "tailwind-merge";
import CameraSkuSectionsNotFound from "./camera-sku-sections-not-found";

const SkuSectionPlanogram = ({
  boundingBox,
}: {
  boundingBox?: { topLeft: Vector2d; bottomRight: Vector2d };
}) => {
  const canvasDimensions = useAppSelector(
    (state) => state.annotation.canvasDimensions
  );
  const dateRange = useAppSelector((state) => state.annotation.dateRange);
  const selectedOrder = useAppSelector(
    (state) => state.annotation.selectedOrder
  );
  const selectedGondola = useAppSelector(selectSelectedGondola);
  const selectedCamera = useAppSelector(selectCurrentCamera);
  const selectedPolygon = useAppSelector(selectSelectedPolygon);
  const isPlanogramSourceImageVisible = useAppSelector(
    selectIsPlanogramSourceImageVisible
  );

  const dispatch = useAppDispatch();

  const { data, isLoading, isFetching } = useGondolasSkuSectionsQuery(
    {
      gondolaId: String(selectedGondola?.id),
      on_datetime: dateRange.start?.toISOString(),
    },
    { skip: !selectedGondola || !selectedOrder }
  );

  const cameraSkuSections = data?.results
    ? getCameraSkuSections(data, selectedCamera)
    : null;

  const planogramViewCameraData = selectedGondola?.cameras?.find(
    (camera) => camera.id === selectedCamera
  );
  const cameraImage = planogramViewCameraData?.imageWebp;

  const planogramImageSize = {
    width: planogramViewCameraData?.width || 1920,
    height: planogramViewCameraData?.height || 1080,
  };

  const onSelectPolygon = (polygon: WithIndex<SkuSection>) => {
    dispatch(setSelectedSkuSectionId(polygon.sku_section_id));
    dispatch(setSelectedPolygon(polygon));
    dispatch(
      setSelectedProduct({
        name: polygon.variant.name,
        imageUrl: polygon.variant.image,
        barcode: polygon.variant.barcode,
        skuId: polygon.variant.id,
        variantId: polygon.variant.variant_id,
        count: 1,
        unitOfMeasurement: polygon.variant.uom,
        unitOfMeasurementInternal: polygon.variant.uom_internal,
      })
    );
  };

  if (isFetching || isLoading) {
    return (
      <Html>
        <Flex
          justify="center"
          align="center"
          className={twMerge(
            "relative h-full bg-black",
            !isPlanogramSourceImageVisible && "hidden"
          )}
        >
          <Spinner size="3" className="text-white" />
          <Text className="text-white" highContrast ml="2">
            Please wait...
          </Text>
        </Flex>
      </Html>
    );
  }

  return (
    <>
      <CameraSkuSectionImage
        imagePath={cameraImage}
        width={canvasDimensions.width}
        height={canvasDimensions.height}
      />
      {!cameraSkuSections || cameraSkuSections.length === 0 ? (
        <Html
          divProps={{
            className: "w-full h-full",
          }}
        >
          <CameraSkuSectionsNotFound />
        </Html>
      ) : (
        <SkuSectionPolygons
          cameraSkuSections={cameraSkuSections}
          boundingBox={boundingBox}
          imageDimensions={canvasDimensions}
          selectedPolygonId={selectedPolygon?.sku_section_id}
          onPolygonClick={onSelectPolygon}
          originalImageSize={planogramImageSize}
        />
      )}
    </>
  );
};

export default SkuSectionPlanogram;
